/**
 * 🏗️ LAYOUT ENGINE
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Layout automático inicial para dashboards estilo Power BI / Looker Studio.
 * 
 * FILOSOFÍA:
 * - Layout inicial inteligente pero NO restrictivo
 * - Los widgets se pueden mover y redimensionar libremente
 * - Permitir superposición (overlap)
 * - El usuario tiene control total del canvas
 * 
 * RESPONSABILIDADES:
 * - Generar layout inicial sugerido
 * - Actualizar posiciones cuando el usuario mueve widgets
 * - Mantener estado del layout
 */

import type { DashboardWidget } from '../../../types/dashboard';

// ════════════════════════════════════════════════════════════════════════
// TIPOS
// ════════════════════════════════════════════════════════════════════════

export interface GridLayout {
  i: string;      // widget id
  x: number;      // posición columna (0-11)
  y: number;      // posición fila (0+)
  w: number;      // ancho en columnas (1-12)
  h: number;      // alto en unidades
  minW?: number;  // ancho mínimo
  minH?: number;  // alto mínimo
  maxW?: number;  // ancho máximo
  maxH?: number;  // alto máximo
}

export interface WidgetWithLayout extends DashboardWidget {
  gridLayout: GridLayout;
  isEmpty?: boolean;
  isWeak?: boolean;
}

// ════════════════════════════════════════════════════════════════════════
// CONSTANTES DE LAYOUT
// ════════════════════════════════════════════════════════════════════════

const GRID_COLS = 12;
const GRID_ROW_HEIGHT = 60;
const GRID_ROW_MARGIN = 16;
const TABLE_ROW_HEIGHT = 38;
const TABLE_HEADER_HEIGHT = 44;
const TABLE_CHROME_HEIGHT = 80;
const TABLE_MIN_ROWS = 2;
const TABLE_MAX_VISIBLE_ROWS = 10;

// Tamaños sugeridos para el layout inicial
const WIDGET_SIZES = {
  metric: {
    w: 4,      // 4 columnas (3 métricas por fila)
    h: 3,      // compacto
    minW: 3,
    maxW: 12,
    minH: 2,
    maxH: 6
  },
  chart: {
    w: 6,      // medio ancho
    h: 6,      // altura estándar
    minW: 4,
    maxW: 12,
    minH: 4,
    maxH: 12
  },
  table: {
    w: 12,     // ancho completo
    h: 10,     // altura estándar
    minW: 6,
    maxW: 12,
    minH: 3,
    maxH: 20
  }
};

// ════════════════════════════════════════════════════════════════════════
// LAYOUT ENGINE
// ════════════════════════════════════════════════════════════════════════

export class EditorialLayoutEngine {
  
  /**
   * Genera layout inicial inteligente desde widgets del backend
   * Layout sugerido: KPIs arriba, gráficos en medio, tablas abajo
   * Pero el usuario puede reorganizar libremente
   */
  static generateLayout(widgets: DashboardWidget[]): WidgetWithLayout[] {
    console.log('🏗️ [LayoutEngine] Generando layout inicial...');
    
    const result: WidgetWithLayout[] = [];
    let currentY = 0;
    
    // 1. Métricas (KPIs) - sugerido arriba
    const metrics = widgets.filter(w => w.type === 'metric');
    const metricWidgets = this.layoutMetrics(metrics, currentY);
    result.push(...metricWidgets);
    
    if (metricWidgets.length > 0) {
      const maxY = Math.max(...metricWidgets.map(w => w.gridLayout.y + w.gridLayout.h));
      currentY = maxY;
    }
    
    // 2. Gráficos (charts) - sugerido en medio
    const charts = widgets.filter(w => w.type === 'bar' || w.type === 'pie' || w.type === 'line');
    const chartWidgets = this.layoutCharts(charts, currentY);
    result.push(...chartWidgets);
    
    if (chartWidgets.length > 0) {
      const maxY = Math.max(...chartWidgets.map(w => w.gridLayout.y + w.gridLayout.h));
      currentY = maxY;
    }
    
    // 3. Tablas - sugerido abajo
    const tables = widgets.filter(w => w.type === 'table');
    const tableWidgets = this.layoutTables(tables, currentY);
    result.push(...tableWidgets);
    
    console.log('✅ [LayoutEngine] Layout generado:', {
      metrics: metricWidgets.length,
      charts: chartWidgets.length,
      tables: tableWidgets.length,
      total: result.length
    });
    
    return result;
  }
  
  /**
   * Layout para métricas (3 por fila)
   */
  private static layoutMetrics(metrics: DashboardWidget[], startY: number): WidgetWithLayout[] {
    return metrics.map((widget, index) => {
      const row = Math.floor(index / 3);
      const col = index % 3;
      const isEmpty = !widget.data || widget.data.length === 0;
      const isWeak = isEmpty || (widget.data[0]?.valor === 0);
      
      return {
        ...widget,
        isEmpty,
        isWeak,
        gridLayout: {
          i: widget.id,
          x: col * WIDGET_SIZES.metric.w,
          y: startY + (row * WIDGET_SIZES.metric.h),
          w: WIDGET_SIZES.metric.w,
          h: WIDGET_SIZES.metric.h,
          minW: WIDGET_SIZES.metric.minW,
          maxW: WIDGET_SIZES.metric.maxW,
          minH: WIDGET_SIZES.metric.minH,
          maxH: WIDGET_SIZES.metric.maxH
        }
      };
    });
  }
  
  /**
   * Layout para gráficos (2 por fila)
   */
  private static layoutCharts(charts: DashboardWidget[], startY: number): WidgetWithLayout[] {
    return charts.map((widget, index) => {
      const row = Math.floor(index / 2);
      const col = index % 2;
      const isEmpty = !widget.data || widget.data.length === 0;
      const isWeak = isEmpty || widget.data.length < 2;
      
      return {
        ...widget,
        isEmpty,
        isWeak,
        gridLayout: {
          i: widget.id,
          x: col * WIDGET_SIZES.chart.w,
          y: startY + (row * WIDGET_SIZES.chart.h),
          w: WIDGET_SIZES.chart.w,
          h: WIDGET_SIZES.chart.h,
          minW: WIDGET_SIZES.chart.minW,
          maxW: WIDGET_SIZES.chart.maxW,
          minH: WIDGET_SIZES.chart.minH,
          maxH: WIDGET_SIZES.chart.maxH
        }
      };
    });
  }
  
  /**
   * Layout para tablas (ancho completo)
   */
  private static layoutTables(tables: DashboardWidget[], startY: number): WidgetWithLayout[] {
    let currentY = startY;
    return tables.map((widget) => {
      const isEmpty = !widget.data || widget.data.length === 0;
      const tableHeightUnits = this.getTableHeightUnits(widget.data?.length ?? 0);
      
      const tableWidget: WidgetWithLayout = {
        ...widget,
        isEmpty,
        isWeak: isEmpty,
        gridLayout: {
          i: widget.id,
          x: 0,
          y: currentY,
          w: WIDGET_SIZES.table.w,
          h: tableHeightUnits,
          minW: WIDGET_SIZES.table.minW,
          maxW: WIDGET_SIZES.table.maxW,
          minH: tableHeightUnits,
          maxH: WIDGET_SIZES.table.maxH
        }
      };
      currentY += tableHeightUnits;
      return tableWidget;
    });
  }

  private static getTableHeightUnits(rowCount: number): number {
    const rows = Math.max(TABLE_MIN_ROWS, rowCount);
    const visibleRows = Math.min(rows, TABLE_MAX_VISIBLE_ROWS);
    const heightPx = TABLE_CHROME_HEIGHT + TABLE_HEADER_HEIGHT + (TABLE_ROW_HEIGHT * visibleRows);
    const rawUnits = Math.ceil((heightPx + GRID_ROW_MARGIN) / (GRID_ROW_HEIGHT + GRID_ROW_MARGIN));
    return Math.min(Math.max(rawUnits, WIDGET_SIZES.table.minH), WIDGET_SIZES.table.maxH);
  }
  
  /**
   * Actualiza layout cuando el usuario hace drag/resize
   * Permite total libertad de movimiento y superposición
   */
  static updateLayout(
    widgets: WidgetWithLayout[],
    newLayouts: GridLayout[]
  ): WidgetWithLayout[] {
    return widgets.map(widget => {
      const newLayout = newLayouts.find(l => l.i === widget.id);
      if (newLayout) {
        return {
          ...widget,
          gridLayout: {
            ...widget.gridLayout,
            x: newLayout.x,
            y: newLayout.y,
            w: newLayout.w,
            h: newLayout.h
          }
        };
      }
      return widget;
    });
  }
}
